import { createContext, useContext, useState, useCallback, useMemo } from 'react';
import { DEFAULT_SURFACE_STYLES } from '../utils/surfaceStyleDefaults';

const STORAGE_KEY = 'tsela-surface-styles';

function loadOverrides() {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        return raw ? JSON.parse(raw) : {};
    } catch {
        return {};
    }
}

const SurfaceStyleCtx = createContext({
    getStyle: key => DEFAULT_SURFACE_STYLES[key] || { color: '#888888', opacity: 0.3 },
    setStyle: () => {},
    resetStyle: () => {},
    resetAll: () => {},
    overrides: {},
});

export function SurfaceStyleProvider({ children }) {
    const [overrides, setOverrides] = useState(loadOverrides);

    const persist = useCallback(next => {
        setOverrides(next);
        localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    }, []);

    const getStyle = useCallback(key => {
        const base = DEFAULT_SURFACE_STYLES[key] || { color: '#888888', opacity: 0.3 };
        const override = overrides[key];
        return override ? { ...base, ...override } : base;
    }, [overrides]);

    const setStyle = useCallback((key, style) => {
        persist({ ...overrides, [key]: { ...overrides[key], ...style } });
    }, [overrides, persist]);

    const resetStyle = useCallback(key => {
        const next = { ...overrides };
        delete next[key];
        persist(next);
    }, [overrides, persist]);

    const resetAll = useCallback(() => persist({}), [persist]);

    const value = useMemo(() => ({ getStyle, setStyle, resetStyle, resetAll, overrides }),
        [getStyle, setStyle, resetStyle, resetAll, overrides]);

    return <SurfaceStyleCtx.Provider value={value}>{children}</SurfaceStyleCtx.Provider>;
}

export const useSurfaceStyles = () => useContext(SurfaceStyleCtx);
